import React, { useEffect, useState, Fragment } from 'react';
import { Clock } from 'lucide-react';
interface CountdownTimerProps {
  /** Duration of the offer in hours. Defaults to 24. */
  durationHours?: number;
  /** localStorage key so the deadline persists across refreshes. */
  storageKey?: string;
  /** Visual variant: 'light' for dark backgrounds, 'dark' for light backgrounds. */
  variant?: 'light' | 'dark';
}
interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}
function getTimeLeft(deadline: number): TimeLeft {
  const diff = deadline - Date.now();
  if (diff <= 0) {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
      expired: true
    };
  }
  const totalSeconds = Math.floor(diff / 1000);
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor(totalSeconds % 3600 / 60),
    seconds: totalSeconds % 60,
    expired: false
  };
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer({
  durationHours = 24,
  storageKey = 'visaprep_offer_deadline',
  variant = 'light'
}: CountdownTimerProps) {
  const [deadline] = useState<number>(() => {
    if (typeof window === 'undefined') return Date.now() + durationHours * 3600 * 1000;
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    // Reuse an existing, still-valid deadline; otherwise start a fresh window.
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
    const fresh = Date.now() + durationHours * 3600 * 1000;
    window.localStorage.setItem(storageKey, fresh.toString());
    return fresh;
  });
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => getTimeLeft(deadline));
  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft(deadline)), 1000);
    return () => clearInterval(id);
  }, [deadline]);
  const isLight = variant === 'light';
  const unitBox = isLight ? 'bg-slate-800 text-white border border-slate-700' : 'bg-slate-900 text-white';
  const labelColor = isLight ? 'text-slate-400' : 'text-slate-500';
  const units: {
    value: number;
    label: string;
  }[] = [{
    value: timeLeft.hours,
    label: 'Horas'
  }, {
    value: timeLeft.minutes,
    label: 'Minutos'
  }, {
    value: timeLeft.seconds,
    label: 'Segundos'
  }];
  return <div className="flex flex-col items-center" role="timer" aria-live="polite" data-id="element-148">
      <div className="flex items-center gap-2 text-yellow-500 font-semibold uppercase tracking-wide text-sm mb-3" data-id="element-149">
        <Clock size={16} data-id="element-150" />
        <span data-id="element-151">
          {timeLeft.expired ? '¡La oferta ha terminado!' : 'Oferta por tiempo limitado · Termina en'}
        </span>
      </div>

      <div className="flex items-end gap-3" data-id="element-152">
        {units.map((unit, i) => <Fragment key={unit.label} data-id="element-153">
            <div className="flex flex-col items-center" data-id="element-154">
              <div className={`${unitBox} w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center text-3xl md:text-4xl font-extrabold tabular-nums shadow-lg`} data-id="element-155">
                {pad(unit.value)}
              </div>
              <span className={`mt-2 text-xs font-medium uppercase ${labelColor}`} data-id="element-156">
                {unit.label}
              </span>
            </div>
            {i < units.length - 1 && <span className="text-3xl md:text-4xl font-extrabold text-yellow-500 pb-7" data-id="element-157">
                :
              </span>}
          </Fragment>)}
      </div>
    </div>;
}